import WechatResult from 'app/components/OrderPayment/Wechat/WechatResult';
import PayverseHeader from 'app/components/PayverseHeader';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isIOS } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

/**
 * 브라우저 언어값을 프로젝트에서 사용하는 언어코드로 변환
 */
const browserTypeJson = (lang: string) => {
  switch (lang) {
    case 'zh-TW':
      return 'cntr';
    case 'zh-CN':
      return 'cnsi';
    case 'en-US':
      return 'en';
    case 'ko':
      return 'ko';
    case 'ko-KR':
      return 'ko';
    case 'ja':
      return 'jp';
    default:
      return '';
  }
};

const WechatResultMob = () => {
  const searchParams = useLocation().search;
  const { t, i18n } = useTranslation();

  /**
   * appScheme:
   * 결제사 앱 종료 후 원래 사용하던 앱으로 복귀시키기 위한 scheme
   */
  const [appScheme, setAppScheme] = useState<string>();

  /**
   * isSubmit:
   * 결제 시작 시 storage에 저장한 값.
   *
   * true  -> 결과페이지로 정상 복귀한 상태로 보고 CommonResult 처리
   * false -> 아직 원래 앱/웹으로 돌아가야 하는 보조 안내 페이지 상태
   */
  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  /**
   * 원래 사용하던 앱으로 이동
   */
  const handleGotoApp = useCallback((scheme: string) => {
    if (!scheme) return;
    window.location.href = scheme;
  }, []);

  /**
   * 최초 진입 시:
   * 1) 결제 시작 여부(storage) 확인
   * 2) 브라우저 언어 기반으로 i18n 언어 설정
   */
  useEffect(() => {
    const checkSubmit = sessionStorage.getItem('wechatSubmit');
    setIsSubmit(checkSubmit === 'submitted');

    const userLanguage = navigator.language;
    const browserLanguage = browserTypeJson(userLanguage);

    if (browserLanguage !== '') {
      i18n.changeLanguage(browserLanguage);
      return;
    }

    i18n.changeLanguage('en');
  }, [i18n]);

  /**
   * query string 에서 appScheme 파라미터 추출
   *
   * 사용 예:
   * ?appScheme=myapp://callback
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(searchParams);
    const scheme = urlParams.get('appScheme');

    if (scheme) {
      setAppScheme(scheme);
    }
  }, [searchParams]);

  /**
   * iOS + isSubmit=false 상태에서만 자동 복귀 시도
   *
   * 해석:
   * - isSubmit === true
   *   => 이미 결과페이지로 정상 복귀했다고 보고 CommonResult 로 진행
   *
   * - isSubmit === false
   *   => 아직 원래 사용하던 앱/웹으로 사용자가 돌아가야 하는 상태
   *   => appScheme 가 있으면 자동 복귀를 시도
   *
   * 단, 아래 조건을 모두 만족할 때만 실행:
   * 1) iOS 일 것
   * 2) 아직 submit 완료 상태가 아닐 것
   * 3) appScheme 가 있을 것
   * 4) 페이지가 실제로 사용자 화면에 보일 것
   * 5) 한 번이라도 백그라운드로 갔다가 돌아온 상태일 것
   * 6) 중복 실행이 아닐 것
   */
  useEffect(() => {
    if (!isIOS) return;
    if (isSubmit) return;
    if (!appScheme) return;

    const timer = window.setTimeout(() => {
      handleGotoApp(appScheme);
    }, 200);

    return () => window.clearTimeout(timer);
  }, [appScheme, isSubmit, handleGotoApp]);

  return (
    <>
      {/**
       * iOS + isSubmit=false 인 경우:
       * 아직 원래 앱/웹으로 돌아가야 하는 안내 화면을 노출
       *
       * - appScheme 가 있으면 자동 복귀 시도 + 수동 복귀 버튼 제공
       * - appScheme 가 없으면 안내 문구만 표시되고,
       *   사용자가 직접 이전 앱/웹으로 돌아가야 함
       */}
      {isIOS && !isSubmit ? (
        <>
          <PayverseHeader logoOn={false} />

          <Page>
            <Card>
              <TopSection>
                <IconWrap>↩</IconWrap>
                <Subtitle>{t('subtitle')}</Subtitle>
                <Title>{t('title')}</Title>
                <Description>{t('description')}</Description>
              </TopSection>

              {appScheme && (
                <BottomSection>
                  <ReturnButton
                    type="button"
                    onClick={() => handleGotoApp(appScheme)}
                  >
                    {t('button')}
                  </ReturnButton>

                  <FooterText>{t('footer')}</FooterText>
                </BottomSection>
              )}
            </Card>
          </Page>
        </>
      ) : (
        /**
         * 그 외 케이스:
         * - isSubmit === true
         * - iOS 가 아님
         *
         * 기존 결과 화면 처리 유지
         */
        <WechatResult />
      )}
    </>
  );
};

export default WechatResultMob;

const Page = styled.div`
  height: 100%;
  padding: 24px 16px 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(to bottom, #f8fafc, #ffffff);
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.12);
  overflow: hidden;
`;

const TopSection = styled.div`
  padding: 32px 24px 24px;
  text-align: center;
`;

const IconWrap = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  border-radius: 9999px;
  background: #163b6a;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
`;

const Subtitle = styled.p`
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  line-height: 1.35;
  font-weight: 700;
  color: #163b6a;
  white-space: pre-line;
`;

const Description = styled.p`
  margin: 14px 0 0;
  font-size: 14px;
  line-height: 1.7;
  color: #475569;
  white-space: pre-line;
`;

const BottomSection = styled.div`
  padding: 0 24px 24px;
`;

const ReturnButton = styled.button`
  width: 100%;
  margin-top: 20px;
  border: none;
  border-radius: 18px;
  background: #163b6a;
  color: #ffffff;
  padding: 16px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;

  &:active {
    transform: scale(0.99);
  }
`;

const FooterText = styled.p`
  margin: 14px 0 0;
  text-align: center;
  font-size: 12px;
  line-height: 1.6;
  color: #94a3b8;
  white-space: pre-line;
`;
